import React, { useState, useEffect } from 'react';
import { Phone, Building2, Calendar, Clock, Mail, RefreshCw, Download, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const { token } = useAuth();

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      toast.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleDelete = async (leadId, leadName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o lead "${leadName}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    setDeleting(leadId);
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lead excluído com sucesso!');
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir lead');
    } finally {
      setDeleting(null);
    }
  };

  const exportToCSV = () => {
    if (leads.length === 0) {
      toast.error('Nenhum lead para exportar');
      return;
    }

    const headers = ['Nome', 'Telefone', 'Empresa', 'Área', 'Dia Preferido', 'Horário', 'Data Cadastro', 'Status'];
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        `"${lead.name}"`,
        `"${lead.phone}"`,
        `"${lead.company}"`,
        `"${lead.area}"`,
        `"${lead.preferred_day}"`,
        `"${lead.preferred_time}"`,
        `"${new Date(lead.created_at).toLocaleDateString('pt-BR')}"`,
        `"${lead.status}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Leads exportados com sucesso!');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendente', className: 'bg-yellow-600' },
      contacted: { label: 'Contatado', className: 'bg-blue-600' },
      converted: { label: 'Convertido', className: 'bg-green-600' },
      lost: { label: 'Perdido', className: 'bg-red-600' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="pt-6 text-center text-gray-400">
          Carregando leads...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white">
          Leads de Diagnóstico ({leads.length})
        </CardTitle>
        <div className="flex gap-2">
          <Button
            onClick={fetchLeads}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-white"
          >
            <RefreshCw className="mr-2" size={16} />
            Atualizar
          </Button>
          <Button
            onClick={exportToCSV}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-white"
          >
            <Download className="mr-2" size={16} />
            Exportar CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {leads.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Mail size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nenhum lead de diagnóstico ainda.</p>
            <p className="text-sm">Os leads aparecerão aqui quando pessoas solicitarem diagnóstico gratuito.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {leads.map((lead) => (
              <div
                key={lead._id}
                className="p-4 bg-zinc-800 rounded-lg border border-zinc-700"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-white font-semibold text-lg">{lead.name}</h3>
                    <div className="flex items-center text-gray-400 text-sm mt-1">
                      <Building2 size={14} className="mr-1" />
                      {lead.company} • {lead.area}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(lead.status)}
                    <Button
                      onClick={() => handleDelete(lead._id, lead.name)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      disabled={deleting === lead._id}
                      title="Excluir lead"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center text-gray-300">
                    <Phone size={14} className="mr-2 text-red-500" />
                    <a href={`tel:${lead.phone}`} className="hover:text-white">
                      {lead.phone}
                    </a>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Calendar size={14} className="mr-2 text-red-500" />
                    {lead.preferred_day}
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Clock size={14} className="mr-2 text-red-500" />
                    {lead.preferred_time}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-zinc-700 text-xs text-gray-500">
                  Cadastrado em: {new Date(lead.created_at).toLocaleString('pt-BR')}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminLeads;
